/**
 * Typed `fetch` wrapper against the Hono API. Pairs with TanStack Query.
 *
 * - Sends the SPA-owned bearer token (localStorage) as `Authorization`.
 * - Throws `ApiError` for non-2xx responses; surface body when JSON.
 */
import { dispatchUnauthorized, getAuthHeaders } from './auth';

/**
 * Paths whose 401 response should NOT trigger the global auth:unauthorized
 * event. Login itself can return 401 for bad credentials — surfacing that as
 * a redirect-to-login would be silly. Logout's 401 means the token's already
 * gone, also nothing to do.
 */
const UNAUTH_DISPATCH_SKIP = new Set<string>(['/v1/auth/login', '/v1/auth/logout']);

export const API_BASE = '/api';

export interface ApiErrorBody {
  error?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | string | null;

  constructor(status: number, message: string, body: ApiErrorBody | string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

interface MutateOptions extends RequestOptions {
  body?: unknown;
}

async function request<T>(
  method: Method,
  path: string,
  options: MutateOptions = {},
): Promise<T> {
  const authHeaders = getAuthHeaders();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeaders,
    ...options.headers,
  };

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] ??= 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body,
    signal: options.signal,
    credentials: 'same-origin',
  });

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const parsed = isJson ? await safeJson(response) : await response.text();
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed
        ? String(parsed.error)
        : null) ||
      response.statusText ||
      `HTTP ${response.status}`;
    if (response.status === 401 && !UNAUTH_DISPATCH_SKIP.has(path)) {
      dispatchUnauthorized();
    }
    throw new ApiError(response.status, message, parsed);
  }

  if (response.status === 204) return undefined as T;
  return (isJson ? await response.json() : await response.text()) as T;
}

async function safeJson(r: Response): Promise<ApiErrorBody | null> {
  try {
    return (await r.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts?: MutateOptions) =>
    request<T>('DELETE', path, opts),
};
